import React, { useEffect, useMemo, useState } from 'react';

import { Input } from 'antd';
import type { TextAreaProps } from 'antd/es/input';

import styles from './DescriptionTextArea.module.css';

export type DescriptionTextAreaProps = Omit<TextAreaProps, 'rows' | 'autoSize'> & {
  showHelper?: boolean;
  helperText?: string;
  errorText?: string;
  minLength?: number;
};

export const DescriptionTextArea: React.FC<DescriptionTextAreaProps> = ({
  value,
  onChange,
  placeholder = '',
  disabled = false,
  status,
  showHelper = false,
  helperText,
  errorText,
  minLength = 0,
  maxLength,
  allowClear = true,
  ...restProps
}) => {
  const [characterCount, setCharacterCount] = useState(0);

  useEffect(() => {
    if (typeof value === 'string') {
      setCharacterCount(value.length);
    } else {
      setCharacterCount(0);
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setCharacterCount(newValue.length);

    if (onChange) {
      onChange(e);
    }
  };

  const defaultHelperText = useMemo(() => {
    if (maxLength && characterCount === maxLength) {
      return `최대 ${maxLength}자까지 입력 가능합니다`;
    }

    if (minLength > 0) {
      return `최소 ${minLength}자 이상 입력해 주세요`;
    }

    return '';
  }, [minLength, maxLength, characterCount]);

  const defaultErrorText = useMemo(() => {
    if (maxLength && characterCount > maxLength) {
      return `최대 ${maxLength}자까지 입력 가능합니다`;
    }

    if (minLength > 0 && characterCount < minLength) {
      return `최소 ${minLength}자 이상 입력해 주세요`;
    }

    return '유효하지 않은 입력입니다';
  }, [minLength, maxLength, characterCount]);

  const finalHelperText = helperText ?? defaultHelperText;
  const finalErrorText = errorText ?? defaultErrorText;
  const hasValidationError = status === 'error';

  return (
    <div className={styles.textareaWrapper}>
      <Input.TextArea
        className={styles.textarea}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        status={status}
        maxLength={maxLength}
        allowClear={allowClear}
        {...restProps}
      />
      {showHelper && (
        <div className={styles.descriptionHelper}>
          <span className={hasValidationError ? styles.helperTextCritical : styles.helperText}>
            {hasValidationError ? finalErrorText : finalHelperText}
          </span>
          <span
            className={`${hasValidationError ? styles.helperTextCritical : styles.helperText} ${styles.characterCount}`}>
            {maxLength && (
              <>
                {characterCount}자/{maxLength}자
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
};
